import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../../chart/bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { formatGateLine } from '../transit-countdown.utils';
import { HdTransitGateInfoSource, buildHdTransitGateInfoPanel } from '../transit-gate-info.utils';
import { HdTransitsViewStateService } from '../transits-view-state.service';
import { HdTransitShiftPopupComponent } from './hd-transit-shift-popup.component';
import { HdTransitShiftPopupData } from './hd-transit-shift-popup.models';

/**
 * Opent de gedeelde transit-shift-popup. Onderkomen: het bestaande
 * floating-panel-systeem (zelfde als de gate-info panels) — draggable,
 * sluiten via het kruisje van de panel-host of Esc, dedup op key
 * (`transit-shift:<planet>:<gate>:<line>`). Floating panels hebben bewust
 * geen backdrop (niet-modaal patroon van de chartomgeving).
 *
 * Open-gedrag in de zwevende transits-view volgt de breedte van die view (zie
 * {@link resolveDetailPlacement}); daarbuiten volgt het de globale
 * `gateInfoOpenBehavior`-setting (dezelfde als
 * de gate-info detailklikken): in de twee `full-docked-*`-modi dockt de popup
 * meteen in de dockarea, anders opent hij als zwevende dialog erbovenop.
 * Zonder geregistreerd dock-target (bv. de standalone transits-pagina) is het
 * altijd een zwevende dialog — de service degradeert dat stil. De waarde is
 * reactief en wordt op de chart-pagina's live gevoed via
 * {@link setOpenBehavior} (hetzelfde patroon als
 * `HdDocPanelService.setLinkOpenMode`); de default komt uit de opgeslagen
 * globale settings.
 *
 * Gedeeld door het Nu & Straks-panel (F2) en de ephemeris-lanes (F3): die
 * roepen enkel {@link open} aan met hun eigen {@link HdTransitShiftPopupData}.
 */
@Injectable({ providedIn: 'root' })
export class HdTransitShiftPopupService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly transitsViewState = inject(HdTransitsViewStateService);

  private readonly openBehaviorSignal = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehaviorSignal.set(behavior);
  }

  /**
   * @param origin optioneel cursor/anker-punt voor smart-placement; zonder
   * origin landt de popup gecentreerd.
   */
  open(data: HdTransitShiftPopupData, origin?: { x: number; y: number }): void {
    const placement = this.resolveDetailPlacement();
    this.floatingPanels.openOrFocus<HdTransitShiftPopupComponent, HdTransitShiftPopupData>({
      key: `transit-shift:${data.planetKey ?? data.planetId}:${data.gate}:${data.line ?? 0}`,
      component: HdTransitShiftPopupComponent,
      data,
      dockTab: { label: formatGateLine(data.gate, data.line) },
      openInitiallyDocked: placement.docked,
      redockOnReopen: placement.docked,
      bypassAutoDock: placement.forceFloating,
      startPosition: origin != null ? { preferred: origin, hint: 'prefer-right-of-anchor' } : 'center',
    });
  }

  /**
   * Volledige gate-info vanuit de transits (poortlink in de verticale
   * weergave), met dezelfde plaatsing als de popup zelf: in de dock-/pagina-
   * context volgt ze de `gateInfoOpenBehavior`-setting, in de zwevende
   * transits-view beslist de breedte (breed = dialog erbovenop, smal = dock).
   */
  openGateInfo(gate: number, source: HdTransitGateInfoSource, origin?: { x: number; y: number }): void {
    const placement = this.resolveDetailPlacement();
    this.floatingPanels.openOrFocus({
      ...buildHdTransitGateInfoPanel(gate, source),
      openInitiallyDocked: placement.docked,
      redockOnReopen: placement.docked,
      bypassAutoDock: placement.forceFloating,
      startPosition: origin != null ? { preferred: origin, hint: 'prefer-right-of-anchor' } : 'center',
    });
  }

  /**
   * Zwevende transits-view: de breedte beslist. In de volle breedte is er geen
   * zichtbare dockarea, dus opent het detail als dialog erbovenop (zoals op de
   * standalone pagina) — en moet de ruimtedruk-heuristiek uitgeschakeld worden,
   * want de transits-dialog vult de viewport en zou anders élke popup naar de
   * dock duwen. In de verkleinde breedte blijft de dockarea ernaast vrij en
   * dokt het detail daar. Buiten die view telt de globale
   * `gateInfoOpenBehavior`-setting.
   */
  private resolveDetailPlacement(): { readonly docked: boolean; readonly forceFloating: boolean } {
    if (this.transitsViewState.floatingViewActive()) {
      const docked = this.transitsViewState.floatingViewNarrow();
      return { docked, forceFloating: !docked };
    }
    const behavior = this.openBehaviorSignal();
    return {
      docked: behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose',
      forceFloating: false,
    };
  }
}
